import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useT } from "@/lib/i18n";
import { getPublicMenu, getMenuImageSignedUrl } from "@/lib/menu-public.functions";
import { ReviewsSection } from "@/components/ReviewsSection";

const menuQuery = (businessId: string, menuSlug: string) =>
  queryOptions({
    queryKey: ["public-menu", businessId, menuSlug],
    queryFn: () => getPublicMenu({ data: { businessId, menuSlug } }),
  });

const LABELS: Record<string, { menu: string; back: string; empty: string }> = {
  cs: { menu: "Menu", back: "Zpět na profil", empty: "Toto menu je prázdné." },
  pl: { menu: "Menu", back: "Powrót do profilu", empty: "To menu jest puste." },
  en: { menu: "Menu", back: "Back to profile", empty: "This menu is empty." },
  es: { menu: "Menú", back: "Volver al perfil", empty: "Este menú está vacío." },
  de: { menu: "Speisekarte", back: "Zurück zum Profil", empty: "Diese Speisekarte ist leer." },
  pt: { menu: "Menu", back: "Voltar ao perfil", empty: "Este menu está vazio." },
  fr: { menu: "Menu", back: "Retour au profil", empty: "Ce menu est vide." },
  hr: { menu: "Jelovnik", back: "Natrag na profil", empty: "Ovaj jelovnik je prazan." },
  sk: { menu: "Menu", back: "Späť na profil", empty: "Toto menu je prázdne." },
  ro: { menu: "Meniu", back: "Înapoi la profil", empty: "Acest meniu este gol." },
  it: { menu: "Menu", back: "Torna al profilo", empty: "Questo menu è vuoto." },
  hu: { menu: "Étlap", back: "Vissza a profilhoz", empty: "Ez az étlap üres." },
  bg: { menu: "Меню", back: "Обратно към профила", empty: "Това меню е празно." },
};

export const Route = createFileRoute("/podnik/$id/menu/$menuSlug")({
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(menuQuery(params.id, params.menuSlug)),
  head: ({ loaderData }) => {
    const title = (loaderData as any)?.menu?.public_name
      ? `${(loaderData as any).menu.public_name} — Menu`
      : "Menu — Cerveza.International";
    return {
      meta: [
        { title },
        { property: "og:title", content: title },
      ],
    };
  },
  component: PublicMenuPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="mb-2 font-serif text-2xl font-bold">Error</h1>
        <p className="mb-4 text-muted-foreground">{error.message}</p>
        <Button onClick={() => { reset(); router.invalidate(); }}>Retry</Button>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl p-8 text-center">
      <h1 className="mb-2 font-serif text-2xl font-bold">Not found</h1>
    </div>
  ),
});

function PublicMenuPage() {
  const { id, menuSlug } = Route.useParams();
  const { lang } = useT();
  const L = LABELS[lang] ?? LABELS.en;

  const { data, isLoading } = useQuery(menuQuery(id, menuSlug));

  const menu = (data as any)?.menu;
  const trs: any[] = useMemo(() => (data as any)?.translations ?? [], [data]);
  const categories: any[] = useMemo(() => (data as any)?.categories ?? [], [data]);
  const catTrs: any[] = useMemo(() => (data as any)?.categoryTranslations ?? [], [data]);
  const items: any[] = useMemo(() => (data as any)?.items ?? [], [data]);
  const itemTrs: any[] = useMemo(() => (data as any)?.itemTranslations ?? [], [data]);
  const itemAllergens: any[] = useMemo(() => (data as any)?.itemAllergens ?? [], [data]);
  const allergens: any[] = useMemo(() => (data as any)?.allergens ?? [], [data]);
  const allergenTrs: any[] = useMemo(() => (data as any)?.allergenTranslations ?? [], [data]);

  const defLang = menu?.default_language ?? "cs";
  const pick = <T,>(list: T[], keyField: string, id: any, field: string): string | null => {
    const preferred = list.find((x: any) => x[keyField] === id && x.language_code === lang);
    if (preferred && (preferred as any)[field]) return (preferred as any)[field];
    const def = list.find((x: any) => x[keyField] === id && x.language_code === defLang);
    if (def && (def as any)[field]) return (def as any)[field];
    const any = list.find((x: any) => x[keyField] === id && (x as any)[field]);
    return any ? (any as any)[field] : null;
  };

  const menuTitle = useMemo(() => {
    if (!menu) return "";
    return pick(trs, "menu_id", menu.id, "title") ?? menu.public_name ?? menu.internal_name ?? "";
  }, [menu, trs, lang]);
  const menuDesc = useMemo(
    () => (menu ? pick(trs, "menu_id", menu.id, "description") : null),
    [menu, trs, lang],
  );
  const footerNote = useMemo(
    () => (menu ? pick(trs, "menu_id", menu.id, "footer_note") : null),
    [menu, trs, lang],
  );

  const allergenLabel = (aid: number): string => {
    const tr = allergenTrs.find((t) => t.allergen_id === aid && t.language_code === lang)
      ?? allergenTrs.find((t) => t.allergen_id === aid && t.language_code === defLang);
    if (tr?.name) return tr.name;
    const a = allergens.find((x) => x.id === aid);
    return a?.code ?? String(aid);
  };

  const itemsByCat = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const it of items) {
      const arr = m.get(it.category_id) ?? [];
      arr.push(it);
      m.set(it.category_id, arr);
    }
    return m;
  }, [items]);

  const allergensByItem = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const ia of itemAllergens) {
      const arr = m.get(ia.item_id) ?? [];
      arr.push(ia.allergen_id);
      m.set(ia.item_id, arr);
    }
    return m;
  }, [itemAllergens]);

  // Signed URLs for item images
  const [signed, setSigned] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    const paths = items.map((i) => i.image_path).filter(Boolean) as string[];
    if (paths.length === 0) {
      // Only reset if we currently hold entries — avoids setState-in-loop
      // when `items` is a fresh reference on every render.
      setSigned((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    (async () => {
      const entries = await Promise.all(
        paths.map(async (p) => [p, await getMenuImageSignedUrl({ data: { path: p } })] as const),
      );
      if (!cancelled) {
        const out: Record<string, string> = {};
        for (const [p, u] of entries) if (u) out[p] = u;
        setSigned(out);
      }
    })();
    return () => { cancelled = true; };
  }, [items]);

  const fmtPrice = (v: number | null | undefined) => {
    if (v == null) return null;
    try {
      return new Intl.NumberFormat(lang, { style: "currency", currency: menu?.currency_code ?? "CZK" }).format(Number(v));
    } catch {
      return `${v} ${menu?.currency_code ?? ""}`.trim();
    }
  };

  if (isLoading) {
    return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!menu) {
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="mb-2 font-serif text-2xl font-bold">404</h1>
        <Link to="/podnik/$id" params={{ id }} className="text-brand-terracotta underline">{L.back}</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      <div className="mx-auto max-w-4xl px-3 py-6 sm:px-6 sm:py-8">
        <div className="mb-6 flex items-center gap-2">
          <BackButton />
          <Link
            to="/podnik/$id"
            params={{ id }}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-terracotta"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {L.back}
          </Link>
        </div>

        <Card className="overflow-hidden">
          <div className="p-4 sm:p-6">
            <h1 className="mb-1 font-serif text-2xl font-bold text-brand-earth sm:text-3xl">{menuTitle}</h1>
            {menuDesc && <p className="mb-4 whitespace-pre-line text-foreground/80">{menuDesc}</p>}

            {categories.length === 0 && (
              <p className="text-sm italic text-muted-foreground">{L.empty}</p>
            )}

            <div className="space-y-8">
              {categories.map((cat) => {
                const catName = pick(catTrs, "category_id", cat.id, "name") ?? cat.name ?? "";
                const catDesc = pick(catTrs, "category_id", cat.id, "description");
                const catItems = itemsByCat.get(cat.id) ?? [];
                return (
                  <section key={cat.id}>
                    <h2 className="mb-1 font-serif text-xl font-semibold text-brand-earth">{catName}</h2>
                    {catDesc && <p className="mb-3 text-sm text-muted-foreground">{catDesc}</p>}
                    <ul className="divide-y divide-brand-earth/10">
                      {catItems.map((it) => {
                        const name = pick(itemTrs, "item_id", it.id, "name") ?? "";
                        const desc = pick(itemTrs, "item_id", it.id, "description");
                        const aids = allergensByItem.get(it.id) ?? [];
                        const img = it.image_path ? signed[it.image_path] : null;
                        return (
                          <li key={it.id} className="flex gap-3 py-3">
                            {img && (
                              <img
                                src={img}
                                alt={it.image_alt || name}
                                loading="lazy"
                                className="h-20 w-32 flex-shrink-0 rounded object-cover sm:h-24 sm:w-40"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-3">
                                <h3 className="font-medium text-foreground">{name}</h3>
                                <div className="flex items-baseline gap-2 whitespace-nowrap">
                                  {it.original_price != null && Number(it.original_price) !== Number(it.price) && (
                                    <span className="text-xs text-muted-foreground line-through">{fmtPrice(it.original_price)}</span>
                                  )}
                                  <span className="font-semibold text-brand-terracotta">{fmtPrice(it.price)}</span>
                                </div>
                              </div>
                              {it.portion_size && (
                                <div className="text-xs text-muted-foreground">{it.portion_size}</div>
                              )}
                              {desc && <p className="mt-1 text-sm text-foreground/80">{desc}</p>}
                              {(aids.length > 0 || it.is_vegetarian || it.is_vegan || it.is_gluten_free || it.is_lactose_free) && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {it.is_vegan && <Badge variant="secondary" className="text-xs">Vegan</Badge>}
                                  {it.is_vegetarian && !it.is_vegan && <Badge variant="secondary" className="text-xs">Veg</Badge>}
                                  {it.is_gluten_free && <Badge variant="secondary" className="text-xs">GF</Badge>}
                                  {it.is_lactose_free && <Badge variant="secondary" className="text-xs">LF</Badge>}
                                  {aids.map((aid) => (
                                    <Badge key={aid} variant="outline" className="text-xs">{allergenLabel(aid)}</Badge>
                                  ))}
                                </div>
                              )}
                              <ReviewsSection targetType="menu_item" targetId={it.id} compact />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>

            {footerNote && (
              <p className="mt-6 border-t border-brand-earth/10 pt-4 text-xs italic text-muted-foreground whitespace-pre-line">
                {footerNote}
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
